// Package imports
import {distanceBetween} from 'geofire-common';
import {reactive} from 'vue';

// Services & lib
import {Graphics} from '../GraphicService';
import {gmAPI, MapService} from '../MapService';
import {getStore} from '@/services/store';
import {createFeatureContent} from '@/lib/classes/InfoWindowContent/helpers/feature';
import {SharedMap} from '@/lib/classes/SharedMap';
import {DisplayField} from '@/lib/classes/InfoWindowContent/DisplayField';
import {getFirestoreUpdateFromFeature} from '@/lib/feature-lib';
import {refreshLinkedLabels} from '../GraphicService/LabelGraphic/linkedLabel';
import {updateSubcollectionDocument} from '../FirestoreService';
import {FEATURES_COLLECTION, OVERLAY_COLLECTION, SHAREDMAPS_COLLECTION} from '@/lib/constants';


/**
 * @typedef {import('@/services/GraphicService/Graphic').Graphic} Graphic
 * @typedef {import('./Overlay').Overlay} Overlay
 * @typedef {import('@/lib/filters/subscriptions').SubscriptionEvent} SubscriptionEvent
 * @typedef {(evt: MouseEvent) => Promise<void>} MouseListener
 * @typedef {import('@/services/GraphicService/Graphic').Graphic} Graphic
 * @typedef {import('@/services/GraphicService/GraphicService').GraphicData} GraphicData
 * @typedef {import('@/lib/classes/InfoWindowContent/InfoWindowContent').Comp} Comp
 * @typedef {import('@/services/RefreshService/types').SourceFeatureMetadata} SourceFeatureMetadata
 *
 * @typedef {object} FeatureData
 * @property {string} id Firestore ID.
 * @property {string} name Display name.
 * @property {string} geohash Geohash representing location of feature's center.
 * @property {google.maps.LatLngBoundsLiteral} bounds Geographic bounds of the feature.
 * @property {GraphicData} graphicData The data for this feature's graphic.
 * @property {boolean} [visible] Whether the feature is currently visible. Defaults to `true` if not provided.
 * @property {boolean} [defaultVisibility] Whether the feature is visible by default. Defaults to `true`
 * @property {Array<DisplayField>} [displayFields] custom fields
 * @property {Array<Comp>} [comps] custom comps
 * @property {number|null} [toolBoxMinZoom] The min zoom level for rendering the Toolbox feature graphic.
 * Null if not a toolbox feature
 * @property {boolean} [isTaxFeature] Whether this feature belongs to a tax overlay
 * @property {boolean} [newFeature] Whether this is a new feature
 * @property {SourceFeatureMetadata} [originalFeatureMetadata] Metadata from the original feature, if this feature
 * was exported from BMAPS originally.
 * @property {import('@/services/RefreshService/types').FieldSelections} [fieldSelections]
 *   The field selections for this feature when it is refreshed.
 *
 * @typedef {object} FeatureInfoWindowEventPayload
 * @property {object} toolbar
 * @property {object[]} toolbar.toolbarOptions
 * @property {google.maps.LatLng} location
 *
 * @typedef {object} FeatureInfoWindowEvent
 * @property {FeatureInfoWindowEventPayload|null} [payload] The content of the event.
 * @property {string} [type] The type of event.
 * @property {number|null} [insertIndex] The index at which to insert the info window content.
 */

/**
 * A handler for a feature click event.
 * @callback FeatureClickHandler
 * @param {MouseEvent|google.maps.MapMouseEvent} event The click event.
 */

/** Features that can remain in an editable state after the Info Window is closed. */
const NO_IW_EDITABLE_FEATURES = ['polygon', 'polyline', 'png', 'route', 'blockPlan-singleLoad', 'blockPlan-crossDock'];

class Feature {
  /**
   * A Feature represents any type of graphic and handles passing a graphic's metadata coming from firestore
   * to the appropriate create<Type> function depending on the feature's type.
   * @constructor
   * @param {FeatureData} data A feature's data coming from Firestore
   * @param {Overlay | SharedMap} parent Optional, the parent this feature belongs to. If omitted, it is assumed
   * the Feature and its Graphic are being maintained by some other mechanism, such as a service.
   */
  constructor(data, parent = null) {
    /**
     * @type {Overlay | SharedMap | null} The parent object for this feature.
     */
    this.parent = parent;
    /** @type {string} Firestore ID of this feature */
    this.id = data.id;
    /** @type {string} Display name for this feature */
    this.name = data.name ?? null;
    /** @type {boolean} Indicates if feature belongs to a tax overlay */
    this.isTaxFeature = data.isTaxFeature ?? false;
    /**
     * @type {Array<DisplayField>} Saved fields from firestore that should be displayed in an infowindow, future
     * implementation
     */
    this.displayFields = data.displayFields ?
        reactive(data.displayFields?.map((field) => deepCopyDisplayField(field))) :
        reactive([]);
    this.newFeature = data.newFeature ?? false;
    /** @type {Array<Comp>} Saved comp fields from firestore */
    this.comps = data.comps ? data.comps.map((comp) => ({
      ...comp,
      displayFields: comp.displayFields?.map((field) => deepCopyDisplayField(field)) ?? [],
    })) : null;
    /**
     * @type {boolean} The default visibility of a feature, indicates if the feature can be shown to unauthenticated
     * users.
     */
    this.defaultVisibility = data.defaultVisibility ?? true;
    /** @type {boolean} The visibility of an individual feature */
    this.visible = this.defaultVisibility;
    /** @type {number|null} The min zoom for the Toolbox feature. Null if not a toolbox feature  */
    this.toolBoxMinZoom = data.toolBoxMinZoom ?? null;

    if (!data.bounds || !('north' in data.bounds) || !('east' in data.bounds) || !('south' in data.bounds) || !('west' in data.bounds)) {
      throw new Error('Invalid feature data: `bounds` with `north`, `east`, `south`, and `west` required to define feature center.');
    }
    /** @type {google.maps.LatLng} The Google Maps API LatLng center of this feature. */
    this.center = MapService.calculateCenter(data.bounds);

    /** @type {string} Geohash representing the location of the center of this feature, used in geo queries. */
    this.geohash = data.geohash;
    // geohash is required for querying. If it is not supplied, throw an error.
    if (!this.geohash) {
      throw new Error('Invalid feature data: `geohash` required to query for feature.');
    }

    /** @type {google.maps.LatLngBoundsLiteral} */
    this.bounds = data.bounds;

    if (data.buildingNumber) {
      /** @type {number} */
      this.buildingNumber = data.buildingNumber ?? null;
    }

    // New Block Plans should have a unique building number
    if (data.graphicData?.featureType?.includes('blockPlan') && !data.graphicData?.buildingNumber) {
      const buildingNumbers = Array.from(parent.features.values()).map((feature) => feature.buildingNumber ?? -1);
      const highestBuildingNumber = Math.max(...buildingNumbers, 0);

      this.buildingNumber = highestBuildingNumber + 1;
      this.name = `Block Plan ${this.buildingNumber}`;
      data.graphicData.buildingNumber = this.buildingNumber;

      if (this.toolBoxMinZoom === null) {
        const update = {name: this.name, graphicData: data.graphicData};
        this.update(update);
      }
    }

    // Allows for the feature type to be accessible without needing to await the graphic
    // Not ideal, but significantly improves performance in ShareableMaps
    this.featureType = data.graphicData?.featureType;
    /**
     * Copied off graphicData so the shelf can nest the label without awaiting the graphic. Not saved.
     * @type {import('@/services/GraphicService/LabelGraphic/linkedLabel').LinkedLabelLink|null}
     */
    this.linkedLabel = data.graphicData?.linkedLabel ?? null;

    /** @type {boolean} */
    this.isEditing = false;

    if (this.parent) {
      try {
      /**
       * The Graphical representation of this Feature.
       * @type {Promise<Graphic> | Graphic}
       */
        this.graphic = Graphics.create(parent.id, data.id, {
          ...data.graphicData,
          title: data.name,
          displayFields: this.displayFields,
          comps: this.comps,
        }, false);
      } catch (error) {
        console.error(`Error creating graphic for feature ${data.name} (${data.id}) in overlay ${parent.name} (${parent.id}): ${error}`);
        return;
      }
    }

    if (data.originalFeatureMetadata) {
      this.originalFeatureMetadata = data.originalFeatureMetadata;
    }
    if (data.fieldSelections) {
      this.fieldSelections = data.fieldSelections;
    }

    // Making this object reactive enables watchers to work correctly
    const reactiveThis = reactive(this);

    if (reactiveThis.graphic) {
      // Listen for updates via the child Graphic. Using .then/.catch here because constructor must be synchronous.
      reactiveThis.graphic
        .then((graphic) => {
          reactiveThis.setIcon(graphic.icon, graphic.color);
          graphic.subscribe('graphic_changed', (event) => reactiveThis.updateFromEvent(event));
          graphic.subscribe('open_info_window', (event) => reactiveThis.openInfoWindow(event));
          graphic.subscribe('duplicate_feature', (event) => reactiveThis.parent.duplicateFeature(event));
        })
        .catch((err) => {
          console.error(`Error creating graphic for feature ${data.name} (${data.id}) in overlay ${parent.name} (${parent.id})`, err);
        });
    }

    return reactiveThis;
  }

  /**
   * Sets the graphic for this feature.
   * @param {Graphic | null} graphic
   */
  setGraphic(graphic) {
    this.graphic = graphic;
  }

  /**
   * Sets the z-index of this feature based on its parent's edit mode, its parent's index in the project, and its
   * index in the parent's list of features.
   * @param {boolean} isPriority If true, this Feature's z-index will be set to a higher value.
   * @param {number} parentIndex
   * @param {number} selfIndex
   * @return {Promise<void>}
   */
  async setZIndex(isPriority, parentIndex, selfIndex) {
    // The minimum negative value is used so that feature z-indices are negative.
    // This is done so that built in Google Maps map features retain event functionality.
    // The built in features include: the reports marker, measure tool handles, parcel
    // boundary handles, access route point handles, and boundary filter handles
    const negativeMin = -10000000;
    // construct a number from the edit mode, parent index, and self index
    const zIndex = negativeMin + (parentIndex * 1000) + selfIndex + (isPriority ? 100000 : 0);
    try {
      (await this.graphic).setZIndex(zIndex);
    } catch (err) {
      console.error(`Error setting z-index for feature ${this.name} (${this.id}) in overlay ${this.parent.name} (${this.parent.id}): ${err}`);
    }
  }

  /**
   * Helper to handle persist logic for all update overloads.
   * @param {object} applyChanges The changes to apply to this feature.
   * @return {Promise<Feature>} A promise that resolves to this feature after the changes have been applied and
   * persisted.
   */
  async _applyUpdateAndPersist(applyChanges) {
    // Update center if bounds changed
    if ('bounds' in applyChanges) {
      this.center = MapService.calculateCenter(applyChanges.bounds);
    }

    // Persist changes to Firestore
    const isParentASharedMap = this.parent instanceof SharedMap;
    const collection = isParentASharedMap ? SHAREDMAPS_COLLECTION : OVERLAY_COLLECTION;
    await updateSubcollectionDocument(collection, this.parent.id, FEATURES_COLLECTION, this.id, applyChanges);

    // Don't update the display fields on the feature to plain objects because reactivity
    // with the InfoWindowContent will be lost
    delete applyChanges.displayFields;
    delete applyChanges.comps;

    // Update the local reactive object with the changes
    Object.assign(this, applyChanges);

    // Call update on the parent to update the time and user of the last update
    await this.parent.update({});
    return this;
  }

  /**
   * Indicates that the parent is being updated. This will set the parent's `editedAt` or `editedOn` property
   * to null, indicating that the current data has not yet been saved.
   */
  indicateParentUpdate() {
    const isParentASharedMap = this.parent instanceof SharedMap;

    if (isParentASharedMap) {
      this.parent.editedOn = null;
    } else {
      this.parent.editedAt = null;
    }
    this.newFeature = false;
  }

  /**
   * Updates this Feature in Firestore with the provided data. Data may be any valid feature properties.
   * @overload
   * @param {Partial<FeatureData>} data
   * @returns {Promise<Feature>}
   */
  /**
   * Updates a top-level display field by ID.
   * @overload
   * @param {import('@/lib/classes/InfoWindowContent/DisplayField').DFValue} newDFValue
   * @param {string} topLevelDisplayFieldId
   * @returns {Promise<Feature|void>}
   */
  /**
   * Updates a display field inside a comp by compId and label.
   * @overload
   * @param {import('@/lib/classes/InfoWindowContent/DisplayField').DFValue} newDFValue
   * @param {string} compId
   * @param {string} displayFieldId
   * @returns {Promise<Feature|void>}
   */
  async update(...args) {
    if (args.length === 1) {
      return this.updateFeature(args[0]);
    } else if (args.length === 2) {
      return this.updateDisplayField(args[0], args[1]);
    } else if (args.length === 3) {
      return this.updateCompDisplayField(args[0], args[1], args[2]);
    } else {
      throw new Error('Invalid number of arguments for Feature.update()');
    }
  }

  /**
   * Applies updated data to this feature and persists it to the database.
   * @private
   * @param {Partial<FeatureData>} data The updated data to apply to this feature.
   * @return {Promise<Feature>} A promise that resolves to this feature.
   */
  async updateFeature(data) {
    this.indicateParentUpdate();
    const graphic = await this.graphic;
    if (data?.displayFields) {
      this.displayFields.splice(0, this.displayFields.length, ...data.displayFields);
      graphic.setDisplayFields(this.displayFields);
      await this.refreshLinkedLabels();
    }
    if (data?.comps) {
      if (!this.comps) {
        this.comps = reactive([]);
      }
      this.comps.splice(0, this.comps.length, ...data.comps);
      graphic.setComps(this.comps);
    }
    // anything other than an object (including when no args are passed at all) is treated as an empty object
    if (!data || typeof data !== 'object') {
      data = {};
    }

    // Get firestore compatible update object
    const applyChanges = getFirestoreUpdateFromFeature(data);

    // The icon type and color need to be accessible without awaiting the graphic
    if (data.icon || data.color) {
      this.setIcon(data.icon, data.color);
    }

    return await this._applyUpdateAndPersist(applyChanges);
  }

  /**
   * Updates a top-level display field by ID.
   * @private
   * @param {import('@/lib/classes/InfoWindowContent/DisplayField').DFValue} newDFValue
   * @param {string} topLevelDisplayFieldId
   */
  async updateDisplayField(newDFValue, topLevelDisplayFieldId) {
    if (!newDFValue || !topLevelDisplayFieldId) {
      throw new Error(`Invalid arguments, must supply new display field value and top level display field ID. Received: "${newDFValue}", "${topLevelDisplayFieldId}"`);
    }

    const oldDF = this.displayFields.find((df) => df.uuid === topLevelDisplayFieldId);
    if (!oldDF) {
      console.warn(`Display field with ID ${topLevelDisplayFieldId} not found in feature ${this.name} (${this.id}).`);
      return;
    }

    this.indicateParentUpdate();

    const newDisplayFields = this.displayFields.map((df) => {
      if (df.uuid === topLevelDisplayFieldId) {
        df.updateValue(newDFValue);
      }
      return df;
    });
    this.displayFields.splice(0, this.displayFields.length, ...newDisplayFields);
    const graphic = await this.graphic;
    graphic.setDisplayFields(this.displayFields);
    await this.refreshLinkedLabels();

    // Get firestore compatible update object
    const applyChanges = getFirestoreUpdateFromFeature({
      displayFields: this.displayFields,
    });

    return await this._applyUpdateAndPersist(applyChanges);
  }

  /**
   * Updates a display field inside a comp by compId and displayFieldId.
   * @private
   * @param {import('@/lib/classes/InfoWindowContent/DisplayField').DFValue} newDFValue
   * @param {string} compId
   * @param {string} displayFieldId
   */
  async updateCompDisplayField(newDFValue, compId, displayFieldId) {
    if (newDFValue === null || newDFValue === undefined || !compId || !displayFieldId) {
      throw new Error('Invalid arguments, must supply new display field, compId, and displayFieldId.');
    }

    // Find the comp display field to update
    const comp = this.comps.find((c) => c.id === compId);
    if (!comp) {
      console.warn(`Comp with ID ${compId} not found in feature ${this.name} (${this.id}).`);
      return;
    }
    const oldDF = comp.displayFields.find((df) => df.uuid === displayFieldId);
    if (!oldDF) {
      console.warn(`Display field with ID ${displayFieldId} not found in comp ${comp.name} (${comp.id}) of feature ${this.name} (${this.id}).`);
      return;
    }

    this.indicateParentUpdate();
    comp.displayFields.forEach((df) => {
      if (df.uuid === displayFieldId) {
        df.updateValue(newDFValue);
      }
    });

    // Get firestore compatible update object
    const applyChanges = getFirestoreUpdateFromFeature({
      comps: this.comps,
    });

    return await this._applyUpdateAndPersist(applyChanges);
  }

  /**
   * Delete a top-level display field by label.
   * @overload
   * @param {string} label
   * @return {Promise<Feature>}
   */
  /**
   * Delete a display field inside a comp by compId and label.
   * @overload
   * @param {string} compId
   * @param {string} label
   * @return {Promise<Feature>}
   */
  async deleteDisplayField(...args) {
    /** @type {import('@/lib/classes/FeatureManager/types').FeatureManagerUpdateData} */
    let dbUpdate;
    if (args.length === 1) {
      dbUpdate = await this.deleteTopLevelDisplayField(args[0]);
    } else if (args.length === 2) {
      dbUpdate = await this.deleteCompDisplayField(args[0], args[1]);
    } else {
      throw new Error('Invalid number of arguments for Feature.deleteDisplayField()');
    }
    return await this._applyUpdateAndPersist(dbUpdate);
  }

  /**
   * Finds a display field by label in a given array, checks if it can be deleted, and removes it.
   * @private
   * @param {DisplayField[]} displayFields - The array of display fields to search.
   * @param {string} label - The label of the display field to delete.
   * @param {string} context - A string for logging/error messages (e.g., 'feature' or 'comp').
   * @returns {boolean} - True if a field was deleted, false otherwise.
   * @throws {Error} - If the field is found but cannot be deleted.
   */
  _findAndRemoveDisplayField(displayFields, label, context) {
    const fieldIndex = displayFields.findIndex((df) => df.label === label);

    if (fieldIndex === -1) {
      console.warn(`Display field with label "${label}" not found in ${context}.`);
      return false;
    }

    const fieldToDelete = displayFields[fieldIndex];
    if (fieldToDelete.allowDelete === false) {
      throw new Error(`Field "${label}" in ${context} cannot be deleted.`);
    }

    displayFields.splice(fieldIndex, 1);
    this.indicateParentUpdate();
    return true;
  }

  /**
   * Deletes a top-level display field by label.
   * @private
   * @param {string} label
   */
  async deleteTopLevelDisplayField(label) {
    if (!label) {
      throw new Error('Invalid arguments, must supply display field label.');
    }

    const context = `feature ${this.name} (${this.id})`;
    this._findAndRemoveDisplayField(this.displayFields, label, context);

    const graphic = await this.graphic;
    graphic.setDisplayFields(this.displayFields);
    await this.refreshLinkedLabels();
    return getFirestoreUpdateFromFeature({displayFields: this.displayFields});
  }

  /**
   * Deletes a display field inside a comp by compId and label.
   * @private
   * @param {string} compId
   * @param {string} label
   */
  async deleteCompDisplayField(compId, label) {
    if (!compId || !label) {
      throw new Error('Invalid arguments, must supply compId and display field label.');
    }

    const comp = this.comps.find((c) => c.id === compId);
    if (!comp) {
      console.warn(`Comp with ID ${compId} not found in feature ${this.name} (${this.id}).`);
      return;
    }

    const context = `comp ${comp.name} (${comp.id}) of feature ${this.name} (${this.id})`;
    this._findAndRemoveDisplayField(comp.displayFields, label, context);
    return getFirestoreUpdateFromFeature({comps: this.comps});
  }

  /**
   * A failure is logged rather than failing the feature's own update.
   * @private
   */
  async refreshLinkedLabels() {
    if (!this.parent?.features) return;
    try {
      await refreshLinkedLabels(this);
    } catch (error) {
      console.error(`Error refreshing linked labels for feature ${this.name} (${this.id}) in overlay ${this.parent.name} (${this.parent.id}): ${error}`);
    }
  }

  /**
   * Updates this Feature in Firestore as a result of an event from its child Graphic.
   * @param {SubscriptionEvent} event The event triggering the update.
   * @return {Promise<Feature>} A promise that resolves to this feature.
   */
  async updateFromEvent(event) {
    /** @type {Graphic} */
    let graphic;
    try {
      graphic = await this.graphic;
    } catch (error) {
      console.error(`Error updating feature ${this.name} (${this.id}) in overlay ${this.parent.name} (${this.parent.id}): ${error}`);
      return;
    }

    // If the payload specifies that some display fields need to be recalculated, update them
    event.payload?.recalcDisplayFields?.forEach((updateDisplayField) => {
      const originalFieldIndex = this.displayFields?.findIndex((field) => {
        // Only update the field if the label matches and it is not editable
        return (field.label === updateDisplayField.label) && field.allowEdit === false;
      });
      if (originalFieldIndex > -1) {
        Object.assign(this.displayFields[originalFieldIndex], {value: updateDisplayField?.value});
      }
    });

    // At some point we need to revisit setting display fields and comps on the graphic.
    // The graphic shouldn't care about the display fields, it should just care about the data it needs to display.
    if (this.displayFields) {
      graphic.setDisplayFields(this.displayFields);
      await this.refreshLinkedLabels();
    }
    if (this.comps) {
      graphic.setComps(this.comps);
    }

    // The object may have been included in the event payload. If not, grab the object from `toJSON()`.
    /** @type {import('@/services/GraphicService/Graphic').GraphicExport} */
    const graphicExport = event.payload?.graphicExport ?? graphic;
    return this.update(graphicExport);
  }

  /**
   * Checks the visible and render properties of the Feature's parent, if both are false
   * calls the underlying graphic's setRender method with false. Otherwise calls the underlying graphic's
   * setRender method with the passed in boolean value.
   * @param {boolean} render - Whether the Feature should render on the map
   */
  async setRender(render) {
    try {
      const graphic = await this.graphic;
      if (!this.parent.visible || !this.parent.render || !this.visible) {
        graphic.setMap(null);
      } else {
        graphic.setMap(render ? MapService.getMap() : null);
      }
    } catch (error) {
      console.error(`Error setting render for feature ${this.name} (${this.id}) in overlay ${this.parent.name} (${this.parent.id}): ${error}`);
    }
  }

  /**
   * @param {boolean} visible Whether the feature should be visible
   * Sets the local 'visible' property on the feature and calls the 'setRender' method depending on the current value
   */
  setVisibility(visible) {
    this.visible = visible;
    this.setRender(visible);
  }

  setDefaultVisibility(visible) {
    this.defaultVisibility = visible;
    this.update({defaultVisibility: visible});
  }

  /**
   * Sets the local icon object property on the feature.
   * @param {string} name The name of the graphic's icon.
   * @param {string} color The color of the graphic's icon.
   */
  setIcon(name, color) {
    // Icon needs to be reactive so that updates are reflected in InfoWindowContent
    if (!this.icon) {
      this.icon = reactive({name, color});
    } else {
      if (color) {
        Object.assign(this.icon, {color});
      }
      if (name) {
        Object.assign(this.icon, {name});
      }
    }
  }

  /**
   * Predicate determining whether the feature is within the specified distance of a point.
   * @param {google.maps.LatLng|google.maps.LatLngLiteral} center The latitude and longitude.
   * @param {number} radius The distance in meters (m) to check.
   * @return {boolean} `true` if the feature is within `radius` of `center`; else `false.
   */
  isInRange(center, radius) {
    if (!center || radius === undefined) {
      throw new Error('Invalid arguments, must supply center and radius.');
    }
    if (this.toolBoxMinZoom !== null) {
      return MapService.getMap().getZoom() >= this.toolBoxMinZoom;
    }
    const thisCenter = [this.center.lat(), this.center.lng()];
    const inputCenter = center instanceof gmAPI.LatLng ? [center.lat(), center.lng()] : [center.lat, center.lng];
    return (distanceBetween(thisCenter, inputCenter) * 1000) <= radius;
  }

  /**
   * Adds a listener to this feature's graphic handled by the Event Service.
   * @param {string} event The type of event to listen for.
   * @param {string} handlerName A name identifying this handler for later removal.
   * @param {FeatureClickHandler} handler
   * @return {Promise<Function>} The listener object.
   */
  async addHandler(event, handlerName, handler) {
    try {
      return (await this.graphic).addHandler(event, handlerName, handler);
    } catch (error) {
      console.error(`Error adding listener to feature ${this.name} (${this.id}) in overlay ${this.parent.name} (${this.parent.id})`, error);
    }
    return null;
  }

  /**
   * Removes a listener from this feature's graphic.
   * @param {string} event The type of event to listen for.
   * @param {string} handlerName The name identifying this handler to remove.
   * @param {function(): void} handler The listener object.
   */
  async removeHandler(event, handlerName, handler) {
    try {
      return (await this.graphic).removeHandler(event, handlerName, handler);
    } catch (error) {
      console.error(`Error removing listener from feature ${this.name} (${this.id}) in overlay ${this.parent.name} (${this.parent.id}): ${error}`);
    }
  }

  /**
   * Changes whether this feature's graphic is editable. When editing is disabled, update with any unsaved changes.
   * @param {boolean} enableEditing `true` to enable editing; `false` to disable it.
   * @param {object} [context] Additional context for this call.
   * @return {Promise<void>} A Promise that resolves either immediately or after changes have been updated.
   */
  async setEditing(enableEditing, context) {
    if (enableEditing && NO_IW_EDITABLE_FEATURES.includes(this.featureType) && !MapService.hasListener(`stop_editing_feature_${this.id}`)) {
      MapService.addMapListener(`stop_editing_feature_${this.id}`, 'click', async () => {
        await this.setEditing(false);
        MapService.removeMapListener(`stop_editing_feature_${this.id}`);
      });
    }

    // Delete the feature if it is a new feature and nothing was edited
    if (!enableEditing && this.displayFields.length === 0 && this.newFeature && this.featureType === 'placeMarker') {
      await this.parent.deleteFeature(this.id);
      return;
    }
    /** @type {Graphic} */
    let graphic;
    try {
      graphic = await this.graphic;
    } catch (error) {
      console.error(`Error setting editing for feature ${this.name} (${this.id}) in overlay ${this.parent.name} (${this.parent.id}): ${error}`);
      return;
    }

    if (!enableEditing && !this.isEditing) return;

    // Set editing state as soon as possible to avoid race conditions
    this.isEditing = enableEditing;

    if (!enableEditing) {
      await getStore().dispatch('setEditingFeature', null);
      MapService.removeMapListener(`stop_editing_feature_${this.id}`);
      MapService.removeMapListener(`stop_editing_mousedown_${this.id}`);
      const unsaved = await graphic.setEditing(enableEditing);
      if (unsaved) {
        await this.updateFromEvent();
      }
      return;
    }

    // Race conditions are possible when attempting to deselect a newly uploaded image
    await new Promise((resolve) => setTimeout(async () => {
      await getStore().dispatch('setEditingFeature', {feature: this, context: {...context}});
      resolve();
    }, 40));
  }

  /**
   * Opens an Info Window for this feature.
   * @param {FeatureInfoWindowEvent} event
   */
  openInfoWindow(event) {
    getStore().commit('infowindow/updateInfoWindowContents', {
      content: createFeatureContent(
        this,
        event.payload?.toolbar,
        event.payload?.location,
        event.payload?.openInEditMode ?? false,
      ),
      insertIndex: event.insertIndex ?? null,
      openInEditMode: event.payload?.openInEditMode ?? false,
    });
  }

  addHighlightListeners() {
    if (this.featureType === 'placeMarker') return;

    const mouseoverHandler = async () => {
      // Overlays should only be highlighted when editing
      // SMAPS features should always be highlighted
      if (!this.parent.editMode && this.parent instanceof SharedMap === false) return;
      // Stifle errors occurring when the graphic is still null
      try {
        const graphic = await this.graphic;
        graphic?.setHighlight(true);
      } catch (e) {
        console.warn(`Couldn't set highlight`, e);
      }
    };

    // Create handler for mousing over while editing
    this.addHandler('mouseover', 'overlayFeatureMouseoverHighlight', mouseoverHandler);

    const mouseoutHandler = async () => {
      const selectedContentId = getStore().state.infowindow.selectedContentId;
      if (!this.parent.editMode && selectedContentId === this.id) return;
      // Stifle errors occurring when the graphic is still null
      try {
        const graphic = await this.graphic;
        graphic?.setHighlight(false);
      } catch (e) {
        console.warn(`Couldn't set highlight`, e);
      }
    };

    // Create handler for mousing out while editing
    this.addHandler('mouseout', 'overlayFeatureMouseoutHighlight', mouseoutHandler);
  }

  addEditListeners() {
    const editCallback = () => {
      if (!this.parent.editMode) return;

      const graphic = Graphics.getGraphic(this.parent.id, this.id);
      const toolbar = graphic.getToolbarOptions?.() ?? {toolbarOptions: []};
      const location = graphic.getLocation?.() ?? this.center;
      this.openInfoWindow({payload: {toolbar, location}});
    };
    this.addHandler('click', 'overlayFeatureEditOnClick', editCallback);
  }

  addViewListeners() {
    const viewCallback = () => {
      if (this.isTaxFeature && getStore().state.selectedOverlay?.id !== this.parent.id) return;

      this.openInfoWindow({
        type: 'open_info_window',
        payload: {toolbar: {toolbarOptions: []}},
      });
    };
    this.addHandler('click', 'featureViewOnClick', viewCallback);
  }

  /**
   * Pan and zoom the map to the feature's center
   */
  panAndZoom() {
    const defaultZoom = 15;
    // If the zoom level is lower than default zoom, zoom in to default. Otherwise keep at current zoom level
    const zoom = (MapService.getMap().getZoom() < defaultZoom) ? defaultZoom : MapService.getMap().getZoom();

    const featureOutsideBounds = !MapService.getMap().getBounds().contains(this.center);
    const centerViewport = MapService.getMap().getZoom() < defaultZoom ||featureOutsideBounds;

    // If the overlay is already in view and the zoom is less than the default, no need to center the viewport
    if (this && centerViewport) {
      MapService.centerViewport(this.center, zoom);
    }
  }

  /**
   * Returns a Lat/Lng location for this feature's graphic. This can be used when the location is needed but
   * a click event wasn't provided.
   * @return {Promise<{lat: number, lng: number}>}
   */
  async getLocation() {
    return (await this.graphic).getLocation();
  }
}

/**
 * ASSUMES that a display field is serializable to any depth, only includes properties of DisplayField
 * and recursively copies all properties
 * @param {DisplayField} displayField
 * @return {DisplayField}
 */
const deepCopyDisplayField = (displayField) => {
  if (!displayField || typeof displayField !== 'object') {
    return displayField;
  }
  const copy = {};
  for (const [key] of Object.entries(displayField)) {
    copy[key] = displayField[key];
  }
  return new DisplayField(displayField);
};

export {
  Feature,
  NO_IW_EDITABLE_FEATURES,
};
